import React, { useState, useEffect, useRef } from 'react';
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  Box,
  Button,
  Card,
  CardContent,
  CardActions,
  Chip,
  Tooltip,
  Divider
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import EditIcon from '@mui/icons-material/Edit';
import AddIcon from '@mui/icons-material/Add';
import CategoryIcon from '@mui/icons-material/Category';
import DashboardIcon from '@mui/icons-material/Dashboard';
import DashboardCustomizeIcon from '@mui/icons-material/DashboardCustomize';
import { Category, Dashboard } from '../services/DashboardsService';
import CategoryForm from './CategoryForm';
import DashboardDetailsDialog from './DashboardDetailsDialog';
import DashboardCreateDialog from './DashboardCreateDialog';

interface DashboardCategoriesProps {
  categories: Category[];
  dashboardsByCategory: Record<number, Dashboard[]>;
  onUpdateCategory: (category: Category) => Promise<boolean>;
  onCreateCategory: (category: Omit<Category, 'id'>) => Promise<boolean>;
  onUpdateDashboard: (dashboard: Dashboard) => Promise<boolean>;
  onCreateDashboard?: (dashboard: Partial<Dashboard>) => Promise<boolean>;
  hideCreateCategoryButton?: boolean;
}

const DashboardCategories: React.FC<DashboardCategoriesProps> = ({
  categories,
  dashboardsByCategory,
  onUpdateCategory,
  onCreateCategory,
  onUpdateDashboard,
  onCreateDashboard,
  hideCreateCategoryButton = false
}) => {
  // Store expanded categories in a ref to preserve across re-renders
  const expandedCategoriesRef = useRef<Set<number>>(new Set());
  const [expandedCategories, setExpandedCategories] = useState<Set<number>>(new Set());
  const [editingCategory, setEditingCategory] = useState<Category | null>(null);
  const [isCreating, setIsCreating] = useState<boolean>(false);
  const [selectedDashboard, setSelectedDashboard] = useState<Dashboard | null>(null);
  const [detailsDialogOpen, setDetailsDialogOpen] = useState<boolean>(false);
  const [creatingDashboardForCategory, setCreatingDashboardForCategory] = useState<Category | null>(null);
  const [createDialogOpen, setCreateDialogOpen] = useState<boolean>(false);
  const categoriesRef = useRef<Category[]>([]);

  // Update our refs when props change to help with comparison
  useEffect(() => {
    categoriesRef.current = categories;
  }, [categories]);

  // Handle accordion expansion
  const handleChange = (categoryId: number) => (_event: React.SyntheticEvent, isExpanded: boolean) => {
    setExpandedCategories(prev => {
      const newSet = new Set(prev);
      if (isExpanded) {
        newSet.add(categoryId);
      } else {
        newSet.delete(categoryId);
      }
      // Update the ref as well to preserve across re-renders
      expandedCategoriesRef.current = newSet;
      return newSet;
    });
  };

  // Ensure editing category stays expanded
  useEffect(() => {
    if (editingCategory) {
      setExpandedCategories(prev => {
        const newSet = new Set(prev);
        newSet.add(editingCategory.id);
        expandedCategoriesRef.current = newSet;
        return newSet;
      });
    }
  }, [editingCategory]);

  // Preserve expanded state across category list changes
  useEffect(() => {
    // Re-apply expanded state from ref
    setExpandedCategories(expandedCategoriesRef.current);
  }, [categories]);

  // Start editing a category
  const handleEditCategory = (category: Category, event: React.MouseEvent) => {
    event.stopPropagation(); // Prevent accordion from toggling
    setEditingCategory(category);
  };

  // Start creating a new dashboard for a category
  const handleCreateDashboard = (category: Category, event: React.MouseEvent) => {
    event.stopPropagation(); // Prevent accordion from toggling
    setCreatingDashboardForCategory(category);
    setCreateDialogOpen(true);
  };

  // Handle new dashboard creation
  const handleSaveNewDashboard = async (dashboard: Dashboard) => {
    if (!onCreateDashboard) return false;

    try {
      const success = await onCreateDashboard({
        ...dashboard,
        category: creatingDashboardForCategory || undefined // Convert null to undefined when necessary
      });

      if (success) {
        setCreatingDashboardForCategory(null);
        setCreateDialogOpen(false);
      }
      return success;
    } catch (error) {
      console.error('Error creating dashboard:', error);
      return false;
    }
  };

  // Handle category update
  const handleCategorySave = async (category: Category) => {
    const success = await onUpdateCategory(category);
    if (success) {
      setEditingCategory(null);
    }
    return success;
  };

  // Handle new category creation
  const handleCategoryCreate = async (category: Omit<Category, 'id'>) => {
    const success = await onCreateCategory(category);
    if (success) {
      setIsCreating(false);
    }
    return success;
  };

  // Handle cancel dashboard creation
  const handleCancelDashboardCreate = () => {
    setCreatingDashboardForCategory(null);
    setCreateDialogOpen(false);
  };

  // Handle dashboard update
  const handleUpdateDashboard = async (dashboard: Dashboard) => {
    try {
      const success = await onUpdateDashboard(dashboard);
      if (success) {
        // Close dialog on success handled in the dialog component
      }
      return success;
    } catch (error) {
      console.error('Error updating dashboard:', error);
      return false;
    }
  };

  // Handle opening dashboard details
  const handleOpenDashboardDetails = (dashboard: Dashboard) => {
    setSelectedDashboard(dashboard);
    setDetailsDialogOpen(true);
  };

  // Handle closing dashboard details
  const handleCloseDashboardDetails = () => {
    setDetailsDialogOpen(false);
  };

  // Render dashboard card
  const renderDashboardCard = (dashboard: Dashboard) => (
    <Box
      key={dashboard.id}
      sx={{
        width: { xs: '100%', sm: 'calc(50% - 16px)', md: 'calc(33.333% - 16px)' },
        mb: 2,
        mx: 1,
        opacity: dashboard.deactivated ? 0.7 : 1
      }}
    >
      <Card
        variant="outlined"
        sx={{
          height: '100%',
          display: 'flex',
          flexDirection: 'column'
        }}
      >
        <CardContent sx={{ flexGrow: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
            {dashboard.icon ? (
              <Box sx={{ mr: 1, display: 'flex', alignItems: 'center' }}>
                <img 
                  src={dashboard.icon} 
                  alt={`${dashboard.name} icon`} 
                  style={{ height: 24, width: 24 }} 
                  onError={(e) => {
                    e.currentTarget.style.display = 'none';
                    const fallbackEl = e.currentTarget.nextElementSibling;
                    if (fallbackEl && fallbackEl instanceof HTMLElement) fallbackEl.style.display = 'block';
                  }}
                />
                <DashboardIcon 
                  sx={{ 
                    height: 24, 
                    width: 24, 
                    color: 'primary.main',
                    display: 'none'
                  }} 
                />
              </Box>
            ) : (
              <DashboardIcon sx={{ mr: 1, color: 'primary.main' }} />
            )}
            <Typography variant="h6" component="div" noWrap>
              {dashboard.name}
            </Typography>
          </Box>

          {dashboard.subtitle && (
            <Typography variant="body2" color="text.secondary" gutterBottom>
              {dashboard.subtitle}
            </Typography>
          )}

          <Box sx={{ mt: 1, display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
            <Chip
              size="small"
              label={dashboard.type}
              color={
                  dashboard.type === 'superset'
                  ? 'primary'
                  : 'default'
              }
            />

            {dashboard.deactivated && <Chip size="small" label="DEACTIVATED" color="error" />}
          </Box>
        </CardContent>

        <CardActions>
          <Button 
            size="small" 
            color="primary" 
            onClick={() => handleOpenDashboardDetails(dashboard)}
            startIcon={<DashboardCustomizeIcon fontSize="small" />}
          >
            View Details
          </Button>
        </CardActions>
      </Card>
    </Box>
  );

  // Render "Add new dashboard" card
  const renderAddDashboardCard = (category: Category) => (
    <Box
      sx={{
        width: { xs: '100%', sm: 'calc(50% - 16px)', md: 'calc(33.333% - 16px)' },
        mb: 2,
        mx: 1
      }}
    >
      <Card
        variant="outlined"
        sx={{
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          border: '1px dashed',
          borderColor: 'divider',
          backgroundColor: 'background.paper',
          '&:hover': {
            borderColor: 'primary.main',
            cursor: 'pointer'
          }
        }}
        onClick={(e) => {
          e.stopPropagation();
          handleCreateDashboard(category, e as React.MouseEvent);
        }}
      >
        <CardContent sx={{ 
          flexGrow: 1, 
          display: 'flex', 
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          textAlign: 'center',
          p: 4
        }}>
          <AddIcon 
            sx={{ 
              fontSize: 40, 
              color: 'text.secondary',
              mb: 2
            }} 
          />
          <Typography variant="h6" color="text.secondary">
            Add New Dashboard
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Click to create a new dashboard in this category
          </Typography>
        </CardContent>
      </Card>
    </Box>
  );

  // Split categories into active and deactivated
  const activeCategories = categories.filter(category => !category.deactivated);
  const deactivatedCategories = categories.filter(category => category.deactivated);

  // Sort within each group by orderBy
  const sortedActiveCategories = [...activeCategories].sort((a, b) => a.orderBy - b.orderBy);
  const sortedDeactivatedCategories = [...deactivatedCategories].sort((a, b) => a.orderBy - b.orderBy);

  // Render a category accordion
  const renderCategoryAccordion = (category: Category) => (
    <Box key={category.id} sx={{ position: 'relative', mb: 2 }}>
      <Accordion
        expanded={
          expandedCategories.has(category.id) || editingCategory?.id === category.id
        }
        onChange={handleChange(category.id)}
        sx={{
          width: '100%'
        }}
      >
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          aria-controls={`category-${category.id}-content`}
          id={`category-${category.id}-header`}
          sx={{
            opacity: category.deactivated ? 0.8 : 1
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <CategoryIcon sx={{ mr: 1, color: 'text.secondary' }} />
            <Typography variant="subtitle1">
              {category.name}
              {category.deactivated && (
                <Chip
                  size="small"
                  label="Deactivated"
                  color="error"
                  sx={{ ml: 1, verticalAlign: 'middle' }}
                />
              )}
            </Typography>
            <Tooltip title="Edit category">
              <span
                onClick={e => {
                  e.stopPropagation();
                  handleEditCategory(category, e as React.MouseEvent);
                }}
              >
                <EditIcon
                  fontSize="small"
                  sx={{
                    cursor: 'pointer',
                    color: 'rgba(0, 0, 0, 0.54)',
                    ml: 1.5,
                    '&:hover': {
                      color: 'primary.main'
                    }
                  }}
                />
              </span>
            </Tooltip>
            {category.subtitle && (
              <Typography variant="body2" color="text.secondary" sx={{ ml: 2 }}>
                {category.subtitle}
              </Typography>
            )}
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          {editingCategory?.id === category.id ? (
            <CategoryForm
              initialValues={editingCategory}
              onSave={handleCategorySave}
              onCancel={() => setEditingCategory(null)}
            />
          ) : (
            <Box sx={{ width: '100%' }}>
              {dashboardsByCategory[category.id]?.length > 0 ? (
                <Box
                  sx={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    mx: -1,
                    mt: 1
                  }}
                >
                  {dashboardsByCategory[category.id].map(renderDashboardCard)}
                  {renderAddDashboardCard(category)}
                </Box>
              ) : (
                <Box
                  sx={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    mx: -1,
                    mt: 1
                  }}
                >
                  {renderAddDashboardCard(category)}
                </Box>
              )}
            </Box>
          )}
        </AccordionDetails>
      </Accordion>
    </Box>
  );

  return (
    <Box sx={{ mb: 4 }}>
      {!hideCreateCategoryButton && (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => setIsCreating(true)}>
            Create New Category
          </Button>
        </Box>
      )}

      {isCreating && (
        <CategoryForm
          onSave={handleCategoryCreate}
          onCancel={() => setIsCreating(false)}
          initialValues={{
            name: '',
            shortname: '',
            subtitle: '',
            orderBy: categories.length + 1,
            deactivated: false
          }}
          isNew
        />
      )}

      {sortedActiveCategories.map(renderCategoryAccordion)}

      {sortedDeactivatedCategories.length > 0 && (
        <Box sx={{ mt: 4, mb: 3 }}>
          <Divider sx={{ mb: 2 }} />
          <Typography
            variant="h6"
            color="text.secondary"
            gutterBottom
            sx={{ pl: 2 }}
          >
            Deactivated Categories
          </Typography>
          {sortedDeactivatedCategories.map(renderCategoryAccordion)}
        </Box>
      )}

      {categories.length === 0 && !isCreating && (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <Typography variant="body1" color="text.secondary" gutterBottom>
            No dashboard categories found
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setIsCreating(true)}
          >
            Create New Category
          </Button>
        </Box>
      )}

      <DashboardDetailsDialog
        dashboard={selectedDashboard}
        categories={categories}
        dashboardsByCategory={dashboardsByCategory}
        open={detailsDialogOpen}
        onClose={handleCloseDashboardDetails}
        onUpdate={handleUpdateDashboard}
      />

      <DashboardCreateDialog
        category={creatingDashboardForCategory}
        categories={categories}
        dashboardsByCategory={dashboardsByCategory}
        open={createDialogOpen}
        onClose={handleCancelDashboardCreate}
        onSave={handleSaveNewDashboard}
      />
    </Box>
  );
};

export default DashboardCategories;